import {
	DataSource,
	type DataSourceDataParams,
	type DataSourceLivePaginationCursorFn,
	type DataSourcePropFilterValue,
	type DataSourcePropGroupBy,
	type DataSourceSortInfo,
	InfiniteTable,
	type InfiniteTablePropColumns,
} from "@infinite-table/infinite-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { CompanyFiling } from "epsm-api-types/models";
import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useState,
} from "react";
import { Link } from "react-router";
import { LOCALE } from "@/config";
import type { ElementsState } from "@/context/mosaic/TitleMosaicProvider";
import { getAlign, getParams } from "@/lib/utils";
import { settings } from "@/lib/utils/settings/settings";
import {
	DEFAULT_GRID_PAGE_SIZE,
	DEFAULT_STATE_VALUES,
} from "@/shared/constants/common";
import sprites from "/src/shared/assets/sprite.svg";
import { UseData } from "./UseData";

const useFiltering = true;

const DEFAULT_PAGE_SIZE = DEFAULT_GRID_PAGE_SIZE;
const DEFAULT_FILTER_DELAY = 400;

const getColumns = (
	setColumnOrder: Dispatch<SetStateAction<string[]>>,
): InfiniteTablePropColumns<CompanyFiling> => {
	const columns: InfiniteTablePropColumns<CompanyFiling> = {
		filing_date: {
			defaultFilterable: false,
			header: "File Date",
			field: "filing_date",
			type: "date",
			headerAlign: "start",
			align: getAlign("date"),
			valueGetter: ({ data }) => data.filing_date?.toLocaleDateString(LOCALE),
		},
		report_type: {
			header: "Form",
			field: "report_type",
			headerAlign: "start",
			align: getAlign("num"),
		},
		filing_url: {
			defaultSortable: false,
			defaultFilterable: false,
			header: "Link",
			field: "filing_url",
			headerAlign: "start",
			align: getAlign("str"),
			render: ({ value }) => (
				<Link to={value} target="_blank" rel="noopener noreferrer">
					<svg
						aria-label="link"
						role="img"
						fill="none"
						stroke="#aaa"
						className="w-3 h-3"
					>
						<use xlinkHref={`${sprites}#link`} />
					</svg>
				</Link>
			),
		},
	};
	const columnOrder = Object.keys(columns);

	setColumnOrder(columnOrder);
	return columns;
};

const defaultSortInfo: DataSourceSortInfo<CompanyFiling> = [
	{
		field: "filing_date",
		dir: -1,
	},
];

const defaultFilterValue: DataSourcePropFilterValue<CompanyFiling> | undefined =
	useFiltering ? [] : undefined;

const defaultGroupBy: DataSourcePropGroupBy<CompanyFiling> = [];

let totalCount = 0;

export const Filings = ({ state }: { state: ElementsState }) => {
	const ticker = state?.ticker ?? DEFAULT_STATE_VALUES.ticker;

	const [columnOrder, setColumnOrder] = useState<string[]>([]);
	const { execute, isLoading } = UseData.create().useGetData();

	const getData = async (dataInfo: DataSourceDataParams<CompanyFiling>) => {
		const result = await execute({
			params: getParams(
				dataInfo,
				columnOrder.length ? ["id", ...columnOrder] : undefined,
				DEFAULT_PAGE_SIZE,
				[{ name: "company.ticker", condition: "eq", value: ticker }],
			),
		});
		if (result?.metadata?.total) {
			totalCount = result.metadata.total;
		}
		return {
			data: result?.data || [],
			hasMore: result?.metadata?.pagination?.nextPage,
			totalCount,
		};
	};

	const [_scrollTopId, setScrollTop] = useState(0);

	useEffect(() => {
		// when sorting changes, scroll to the top
		setScrollTop(Date.now());
	}, []);

	const [dataParams, setDataParams] = useState<
		Partial<DataSourceDataParams<CompanyFiling>>
	>({
		groupBy: defaultGroupBy,
		sortInfo: defaultSortInfo,
		filterValue: defaultFilterValue,
		livePaginationCursor: 0,
	});

	const { data, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
		queryKey: [
			"FilingInfo",
			ticker,
			dataParams.groupBy,
			dataParams.sortInfo,
			dataParams.filterValue,
		],
		queryFn: async ({ pageParam = 0 }) => {
			const params: DataSourceDataParams<CompanyFiling> = {
				originalDataArray: [],
				livePaginationCursor: pageParam,
				groupBy: dataParams.groupBy,
				sortInfo: dataParams.sortInfo,
				filterValue: dataParams.filterValue,
			};

			return await getData(params);
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			if (lastPage?.hasMore) {
				return (allPages?.length ?? 0) * DEFAULT_PAGE_SIZE;
			}
			return undefined;
		},
		select: (data) => {
			const flatData = data.pages.flatMap((x) => x.data);

			const result = {
				pages: flatData,
			};

			return result;
		},
	});

	const [columns, setColumns] = useState<
		InfiniteTablePropColumns<CompanyFiling>
	>({});

	useEffect(() => {
		const columnsCall = () => {
			const result = getColumns(setColumnOrder);
			setColumns(result);
		};
		columnsCall();
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: correct deps array
	useEffect(() => {
		if (isFetchingNextPage || isLoading) {
			return;
		}
		if (dataParams.livePaginationCursor !== undefined) {
			fetchNextPage();
		}
	}, [dataParams.livePaginationCursor, fetchNextPage]);

	const onDataParamsChange = useCallback(
		(dataParams: DataSourceDataParams<CompanyFiling>) => {
			const params = {
				groupBy: dataParams.groupBy,
				sortInfo: dataParams.sortInfo,
				filterValue: dataParams.filterValue,
				livePaginationCursor: dataParams.livePaginationCursor,
			};

			setDataParams(params);
		},
		[],
	);

	const livePaginationCursorFn: DataSourceLivePaginationCursorFn<CompanyFiling> =
		useCallback(({ length }) => {
			return length;
		}, []);

	return (
		<div id="full-h-el" className="h-full">
			<DataSource<CompanyFiling>
				data={data?.pages ?? []}
				filterDelay={DEFAULT_FILTER_DELAY}
				primaryKey="id"
				defaultSortInfo={defaultSortInfo}
				defaultFilterValue={defaultFilterValue}
				loading={isLoading || isFetchingNextPage}
				livePagination={true}
				livePaginationCursor={livePaginationCursorFn}
				onDataParamsChange={onDataParamsChange}
			>
				<InfiniteTable<CompanyFiling>
					columns={columns}
					columnOrder={columnOrder}
					onColumnOrderChange={setColumnOrder}
					columnDefaultFlex={1}
					rowHeight={20}
					licenseKey={settings.infiniteTableLicenseKey}
				/>
			</DataSource>
		</div>
	);
};
